import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";

import Button from "~/components/buttons/Button";
import ButtonNewItem from "~/components/buttons/ButtonNewItem";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { addProjectPhasesToQuota } from "~/modules/client/api/quota/quotaApiDispatchers.ts";
import type { QuotaWithRelations } from "~/modules/client/api/quota/quotaTypes.ts";
import QuotaProjectPhaseSelector
	from "~/modules/client/components/ClientDetailsView/components/QuotasTabContent/components/CreateQuotaSidebar/components/QuotaProjectPhaseSelector.tsx";
import SelectedQuotaProjectPhase
	from "~/modules/client/components/ClientDetailsView/components/QuotasTabContent/components/CreateQuotaSidebar/components/SelectedQuotaProjectPhase.tsx";
import type { ProjectPhaseForQuotaAssignment } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import { getFullPhaseTitle } from "~/modules/project/utils/projectPhaseUtils.ts";
import { getFullProjectTitle } from "~/modules/project/utils/projectUtils.ts";


type Props = {
	onCancel: () => void;
	onSuccess: () => void;
	quota: QuotaWithRelations
};

const AddPhaseToQuotaForm = ({
	onCancel,
	onSuccess,
	quota,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [selectedPhasesData, setSelectedPhasesData] = useState<ProjectPhaseForQuotaAssignment[]>([]);
	const [showPhaseSelect, setShowPhaseSelect] = useState<boolean>(false);
	const onSubmit = useCallback(async () => {
		try {
			setIsBusy(true);
			await addProjectPhasesToQuota(quota.clientId, quota.id, selectedPhasesData.map(phase => phase.id));
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [onSuccess, quota.clientId, quota.id, selectedPhasesData]);

	const handleDeletePhaseClick = (phaseId: string) => {
		setSelectedPhasesData(prevState => {
			return prevState.filter(phase => phaseId !== phase.id);
		});
	};

	const assignedPhaseIds = useMemo(() => {
		return quota.projectPhases.map(phase => phase.id)
	}, [quota.projectPhases]);

	if (showPhaseSelect) {
		return <div
			className="auto-grid-sidebar"
		>
			<QuotaProjectPhaseSelector clientId={quota.clientId}
									   excludedPhaseIds={assignedPhaseIds}
									   onCancel={() => setShowPhaseSelect(false)}
									   selectedPhasesData={selectedPhasesData}
									   setSelectedPhasesData={setSelectedPhasesData} />
		</div>;
	}

	return (
		<div
			className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Phase(n) hinzufügen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<FormSectionGroup>
					<FormSection title="Kontingent">
						{quota.title}
					</FormSection>
					<FormSection title="Projektphasen">
						<div className="space-y-4">
							{quota.projectPhases.map(phase => {
								return <SelectedQuotaProjectPhase key={phase.id}
																  phaseId={phase.id}
																  phaseTitle={getFullPhaseTitle(phase)}
																  projectTitle={getFullProjectTitle(phase.project)} />;
							})}
							{selectedPhasesData.length > 0 && selectedPhasesData.map(phase => {
								return <SelectedQuotaProjectPhase key={phase.id}
																  onDelete={handleDeletePhaseClick}
																  phaseId={phase.id}
																  phaseTitle={phase.phaseTitle}
																  projectTitle={phase.projectTitle} />;
							})}
							<ButtonNewItem
								size="sm"
								theme="inline"
								onClick={() => setShowPhaseSelect(true)}
							>{selectedPhasesData.length > 0 && "Weitere "} Projektphase/n auswählen</ButtonNewItem>
						</div>
					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<Button onClick={onSubmit}
						disabled={selectedPhasesData.length === 0}>
					{`${selectedPhasesData.length > 0 ? selectedPhasesData.length + " " : ""} Phase${selectedPhasesData.length > 1 ? "n" : ""} hinzufügen `}
				</Button>
				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
			</SidebarFooter>
		</div>);
};

export default AddPhaseToQuotaForm;